import { useSyncExternalStore } from 'react';
import { readScreenCache } from './screenDataCache';
import { IS_APP_BUILD } from './buildTarget';

/* dashboard-rediseno (4.2 del encargo): LOS CREDITOS DE LA BARRA, UN ESTADO DE LA SESION.

   La barra de arriba ensena los creditos en TODAS las pantallas, y hasta esta tanda solo el Panel los pedia
   (`/auth/plan-info` y `/documents/usage`). Este modulo es el estado compartido: lo llena el Panel con lo que ya
   pide (`publicarCreditos`, en su carga de siempre y con la copia de la bodega cuando pinta sin red), y si alguien
   entra directo a otra pantalla la barra hace UNA carga (`cargarCreditosSiFaltan`). Navegar no pide nada.

   EL CLIENTE SE PIDE AL LLAMAR, NO ARRIBA: este modulo lo consume la barra del chasis, y un `import` de `./api`
   arriba arrastra `src/i18n.js` a toda suite que monte el chasis sin doblar `lib/api` (la mina de la tanda 28b,
   `lib/referidos.js`). Mismo cliente y misma sesion; solo cambia cuando se resuelve el modulo.

   Sin dato todavia, `listo` es falso y la barra reserva el lugar del chip vacio. */

function cliente() { return require('./api').default; }

const VACIO = Object.freeze({ planInfo: null, usage: null, sinRed: false, listo: false });

let estado = VACIO;
let cargaEnVuelo = null;
let generacion = 0;
const oyentes = new Set();

function avisar() {
  oyentes.forEach((fn) => fn());
}

function suscribir(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

function leer() {
  return estado;
}

/** El Panel (o la carga de la barra) publica lo que tiene. Sin `planInfo` ni `usage` no cambia nada: un fallo no
 *  borra lo que ya se ve. */
export function publicarCreditos({ planInfo, usage, sinRed = false } = {}) {
  if (!planInfo && !usage) return;
  estado = {
    planInfo: planInfo || estado.planInfo,
    usage: usage || estado.usage,
    sinRed: Boolean(sinRed),
    listo: true,
  };
  avisar();
}

/** Lo que ve la barra. Cambia solo cuando alguien publica u olvida. */
export function useCreditosDeLaSesion() {
  return useSyncExternalStore(suscribir, leer, leer);
}

/** Lee el estado sin suscribirse (pruebas y decisiones fuera de React). */
export function creditosActuales() {
  return estado;
}

/** La copia que el Panel deja en el cache de pantallas (la misma de su primer pintado). Sirve para no pedir lo
 *  que el Panel ya tiene en la mano: es memoria, no red. */
function copiaDelPanel() {
  const entrada = readScreenCache('dashboard');
  const datos = entrada?.data;
  if (!datos || !datos.planInfo || !datos.usage) return null;
  return { planInfo: datos.planInfo, usage: datos.usage, fresca: Boolean(entrada.fresh) };
}

/** En la app sin red: la ultima foto {planInfo, usage} que el Panel guardo en la bodega (la misma que el Panel
 *  pinta cuando arranca sin red), marcada `sinRed`. Solo si no hay nada que ensenar todavia. */
function copiaDeLaBodega(miGeneracion) {
  return import('./warehouse/snapshots')
    .then((m) => m.readPlanSnapshot())
    .then((foto) => {
      if (!foto || miGeneracion !== generacion || estado.listo) return null;
      publicarCreditos({ planInfo: foto.planInfo, usage: foto.usage, sinRed: true });
      return estado;
    })
    .catch(() => null);
}

/** Vuelve a pedir los dos datos de hoy y los publica. Un fallo deja lo que habia. */
export function refrescarCreditos(user) {
  if (!user) return Promise.resolve(null);
  if (cargaEnVuelo) return cargaEnVuelo;
  const miGeneracion = generacion;
  const api = cliente();
  cargaEnVuelo = Promise.all([api.get('/auth/plan-info'), api.get('/documents/usage')])
    .then(([planRes, usageRes]) => {
      if (miGeneracion !== generacion) return null;
      publicarCreditos({ planInfo: planRes?.data, usage: usageRes?.data, sinRed: false });
      return estado;
    })
    .catch(() => (IS_APP_BUILD && miGeneracion === generacion && !estado.listo ? copiaDeLaBodega(miGeneracion) : null))
    .finally(() => {
      if (miGeneracion === generacion) cargaEnVuelo = null;
    });
  return cargaEnVuelo;
}

/** La primera carga cuando se entra directo a una pantalla que no es el Panel. Una sola vez: con dato no pide
 *  nada, y con la copia fresca del Panel en memoria tampoco. */
export function cargarCreditosSiFaltan(user) {
  if (!user) return Promise.resolve(null);
  if (estado.listo) return Promise.resolve(estado);
  const copia = copiaDelPanel();
  if (copia) {
    publicarCreditos(copia);
    if (copia.fresca) return Promise.resolve(estado);
  }
  return refrescarCreditos(user);
}

/** Lo que el Panel ya dejo en memoria, sin red: para que en el Panel el chip no espere a una carga que el Panel
 *  se salto porque su copia estaba fresca. */
export function tomarCopiaDelPanel() {
  if (estado.listo) return false;
  const copia = copiaDelPanel();
  if (!copia) return false;
  publicarCreditos(copia);
  return true;
}

/** De quien son los creditos que se guardan. Vive en el modulo y no en la barra, porque la barra se desmonta al
 *  cerrar sesion y la que se monta despues no sabria de quien eran. */
let dueno;

/** La barra avisa quien esta en la sesion (o nadie). Si cambio, lo de la sesion anterior se olvida. */
export function asegurarDueno(idDelUsuario) {
  const id = idDelUsuario ?? null;
  if (dueno !== undefined && dueno !== id) olvidarCreditos();
  dueno = id;
}

/** Al cerrar sesion o cambiar de usuario: lo de la sesion anterior no se ensena. */
export function olvidarCreditos() {
  generacion += 1;
  cargaEnVuelo = null;
  if (estado === VACIO) return;
  estado = VACIO;
  avisar();
}
